"use client";

import BaseEditor from "@/components/common/editors/base-editor";
import EditorMediaAndLinksPreview from "@/components/common/editors/base-editor/components/editor-media-and-links-preview";
import EditorTypeOptions from "@/components/common/editors/base-editor/components/editor-type-options";
import { Checkbox, Input, Label } from "@/components/ui";
import { SelectDropdown } from "@/components/ui/select-dropdown";
import { useCommunitiesListingQuery } from "@/hooks/queries/useCommunitiesListingQuery";
import { POSTS_API } from "@/lib/apis";
import { GLOBAL_FEED_SLUG } from "@/lib/constants";
import { useAuth } from "@/providers/auth-provider";
import { useCommunityContext } from "@/providers/community-provider";
import { useCustomEditor } from "@/providers/custom-editor-provider";
import { TCommunityJoinedListingRecord, TCommunityListingRecord } from "@/types/community";
import { MentionProp, TCommunityEditPost, TCommunityNewPost } from "@/types/post";
import { useQueryClient } from "@tanstack/react-query";
import { JSONContent } from "@tiptap/react";
import { Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

type CommunityOption = {
	id: string;
	name: string;
	slug: string;
};

const CreatePostForm = ({
	defaultValues,
	mode = "create",
	onSubmit,
}: {
	defaultValues?: TCommunityEditPost;
	mode?: "create" | "edit";
	onSubmit: (success: boolean) => void;
}) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const { communityInfo, channelInfo } = useCommunityContext();
	const {
		editorType,
		setImageViewType,
		imageViewType,
		images,
		linkCards,
		addLinkCard,
		setImages,
		clearContent,
	} = useCustomEditor();

	const { isPublic } = useCommunityContext();
	const shouldShowCommunityDropdown = !communityInfo?.slug || isPublic;

	const [selectedCommunitySlug, setSelectedCommunitySlug] = useState<string>(
		communityInfo?.slug || GLOBAL_FEED_SLUG
	);

	const { data, isLoading: isCurrentCommunityLoading } = useCommunitiesListingQuery(
		communityInfo?.slug,
		{
			enabled: !!communityInfo?.slug,
		}
	);

	const { data: selectedCommunityData, isLoading: isSelectedCommunityLoading } =
		useCommunitiesListingQuery(selectedCommunitySlug, {
			enabled: shouldShowCommunityDropdown && !!selectedCommunitySlug,
		});

	const { moderatorChannels, moderatorCommunities, isFetchingModeratorData } =
		useCommunityContext();

	const currentCommunityChannels = useMemo(
		() =>
			(data?.data?.find((item) => item.title === "Channels")?.data as TCommunityListingRecord[]) ||
			[],
		[data]
	);

	const selectedCommunityChannels = useMemo(
		() =>
			(selectedCommunityData?.data?.find((item) => item.title === "Channels")
				?.data as TCommunityListingRecord[]) || [],
		[selectedCommunityData]
	);

	const channels = shouldShowCommunityDropdown
		? selectedCommunityChannels
		: currentCommunityChannels;

	const channelOptions = useMemo(() => {
		return channels.map((channel) => ({
			id: channel.slug,
			name: channel.name,
		}));
	}, [channels]);

	const communityOptions: CommunityOption[] = useMemo(() => {
		const joinedCommunities = data?.data?.find((item) => item.title === "Joined")?.data || [];
		return (joinedCommunities as TCommunityJoinedListingRecord[]).map((community) => ({
			id: community.slug,
			name: community.name,
			slug: community.slug,
		}));
	}, [data]);

	const emptyEditorContent = {
		title: "",
		htmlContent: "",
		jsonContent: undefined,
		images: [],
		links: [],
		mentions: [],
	};

	const initialFormState: TCommunityEditPost | TCommunityNewPost = defaultValues ?? {
		communitySlug: selectedCommunitySlug,
		channelSlug: "",
		...emptyEditorContent,
		notifyMembers: false,
		imageViewType,
		postType: editorType === "discussion" ? "DISCUSSION" : "ARTICLE",
	};

	const areChannelsLoading = shouldShowCommunityDropdown
		? isSelectedCommunityLoading
		: isCurrentCommunityLoading;

	const [form, setForm] = useState<TCommunityEditPost | TCommunityNewPost>(initialFormState);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [titleError, setTitleError] = useState<string | undefined>(undefined);
	const [contentError, setContentError] = useState<string | undefined>(undefined);
	const [isFormValid, setIsFormValid] = useState<boolean>(false);
	const [isContentModified, setIsContentModified] = useState(false);

	const handleOnTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		const maxQuestionLength = 300;

		if (value.trim().length === 0) {
			setTitleError("Title should not be empty");
			setForm({ ...form, title: "" });
			return;
		} else if (value.trim().length > maxQuestionLength) {
			setTitleError(`Text is too long (max ${maxQuestionLength} characters), please shorten it`);
		} else {
			setTitleError(undefined);
			setForm({ ...form, title: value });
			setIsFormValid(true);
		}
	};

	const handleCommunityChange = (communitySlug: string) => {
		setSelectedCommunitySlug(communitySlug);
		setForm({
			...form,
			communitySlug,
			channelSlug: "",
		});
	};

	const clearEnteredContent = () => {
		setForm({ ...form, ...emptyEditorContent });
		clearContent();
	};

	const handleContentChange = ({
		html,
		json,
		mentions = [],
	}: {
		html: string;
		json: JSONContent;
		mentions?: MentionProp[];
	}) => {
		const maxContentLength = 30000;
		const trimmedHTMLContent = html.replace(/<p>|<\/p>/g, "").trim();

		const contentPresent = trimmedHTMLContent;
		const isEmpty = trimmedHTMLContent === "";

		if (!isContentModified && trimmedHTMLContent !== form.htmlContent?.replace(/<p>|<\/p>/g, "")) {
			setIsContentModified(true);
		}

		if (isContentModified || contentPresent) {
			if (isEmpty) {
				setContentError("Details should not be empty");
			} else if (trimmedHTMLContent.length > maxContentLength) {
				setContentError(`Text is too long (max ${maxContentLength} characters), please shorten it`);
				setIsFormValid(false);
			} else {
				setContentError(undefined);
				setForm({
					...form,
					htmlContent: html,
					jsonContent: json,
					mentions,
				});
				setIsFormValid(true);
			}
		}
	};

	const handleCreatePost = async (e: React.FormEvent<HTMLButtonElement>) => {
		e.preventDefault();

		try {
			setIsLoading(true);

			const body: TCommunityNewPost | TCommunityEditPost = {
				...form,
				postType: editorType === "article" ? "ARTICLE" : "DISCUSSION",
				images,
				links: linkCards,
			};

			const response = await fetch(POSTS_API, {
				method: mode === "create" ? "POST" : "PATCH",
				body: JSON.stringify(body),
			});

			const data = await response.json();

			if (response.ok) {
				toast.success(data?.message);

				queryClient.invalidateQueries({
					queryKey: ["posts", communityInfo?.slug, channelInfo?.slug],
				});
			} else {
				toast.error(data?.message);
			}

			onSubmit(data.success);
		} catch (error) {
			toast.error("Something went wrong, please try again");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (mode === "edit" && defaultValues) {
			if (images.length === 0) {
				setImages(defaultValues.images || []);
			}

			if (linkCards.length === 0) {
				for (const link of defaultValues.links || []) {
					addLinkCard(link.url);
				}
			}
			setImageViewType(defaultValues.imageViewType ?? "carousel");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (channels.length > 0 && !form.channelSlug && !areChannelsLoading) {
			setForm((prev) => ({
				...prev,
				channelSlug: channels[0].slug,
			}));
		}
	}, [channels, form.channelSlug, areChannelsLoading]);

	useEffect(() => {
		if (areChannelsLoading) return;

		setForm((prev) => ({
			...prev,
			communitySlug: selectedCommunitySlug,
		}));
	}, [selectedCommunitySlug, areChannelsLoading]);

	useEffect(() => {
		if (areChannelsLoading || channels.length === 0) return;

		setForm((prev) => {
			if (mode === "edit" && defaultValues?.channelSlug) {
				return prev;
			}

			if (!prev.channelSlug) {
				const currentChannel = channelInfo?.slug
					? channels.find((ch) => ch.slug === channelInfo.slug)
					: null;

				const selectedChannel = currentChannel || channels[0];

				return {
					...prev,
					channelSlug: selectedChannel?.slug || "",
				};
			}
			return prev;
		});
	}, [channels, areChannelsLoading, channelInfo?.slug, mode, defaultValues?.channelSlug]);

	useEffect(() => {
		const trimmedHTMLContent = form.htmlContent.replace(/<p>|<\/p>/g, "").trim();
		const isContentInvalid = trimmedHTMLContent === "" || trimmedHTMLContent.startsWith(" ");
		const isValid =
			!isContentInvalid &&
			form.title.trim() !== "" &&
			!!form.channelSlug &&
			!areChannelsLoading &&
			!isFetchingModeratorData;

		setIsFormValid(isValid);
	}, [form.title, form.htmlContent, form.channelSlug, areChannelsLoading, isFetchingModeratorData]);

	const isSelectedChannelModerator = () =>
		moderatorChannels.map((e) => e.slug).includes(form.channelSlug);
	const isCommunityModerator = () =>
		moderatorCommunities.map((e) => e.slug).includes(form.communitySlug);

	return (
		<div className="rounded-lg">
			{shouldShowCommunityDropdown && (
				<div className="mb-4">
					<Label>
						<span>Choose a community to post</span>
					</Label>

					<SelectDropdown
						valueType="id"
						buttonLabel={isFetchingModeratorData ? "Loading communities..." : "Select Community"}
						items={communityOptions}
						selectedItem={selectedCommunitySlug}
						setActiveItems={handleCommunityChange}
						disabled={isFetchingModeratorData || communityOptions.length === 0}
					/>
				</div>
			)}

			<div className="mb-4">
				<Label>
					<span>Choose a channel to post</span>
				</Label>

				<SelectDropdown
					valueType="id"
					buttonLabel={areChannelsLoading ? "Loading channels..." : "Select Channel"}
					items={areChannelsLoading ? [] : channelOptions}
					selectedItem={form.channelSlug}
					setActiveItems={(value) => setForm({ ...form, channelSlug: value })}
					disabled={areChannelsLoading || isFetchingModeratorData}
				/>
			</div>

			<EditorTypeOptions
				isEditMode={mode === "edit"}
				// TODO (zeekhuge): Ideally, the 2 types of the values ARTICLE vs article should be the same enum
				defaultEditorType={defaultValues?.postType === "ARTICLE" ? "article" : "discussion"}
				onEditorTypeChange={clearEnteredContent}
			/>

			<Input
				type="text"
				value={form.title}
				placeholder="Title"
				classNames={{
					container: titleError ? "mb-0" : "mb-4",
					input: "sm:text-base px-4 py-3",
					inputContainer: "shadow-none ring-gray-200",
				}}
				onChange={handleOnTitleChange}
				required
			/>

			{titleError && (
				<span className="mb-4 mt-1 block text-xs font-light text-red-500">{titleError}</span>
			)}

			{/* Custom media and links preview */}
			<EditorMediaAndLinksPreview />

			<div className="my-4">
				<BaseEditor
					content={form.jsonContent}
					handleOnUpdate={handleContentChange}
					placeholder={
						editorType === "discussion" ? "Start a discussion" : "Write your article here"
					}
					classNames={{
						editor: "focus-visible:outline-none border rounded-md p-2 bg-white",
					}}
				/>

				{contentError && (
					<span className="mb-4 mt-1 block text-xs font-light text-red-500">{contentError}</span>
				)}
			</div>

			<div className="mb-4">
				{/* TODO: Show this for moderators / community admins */}
				{mode === "create" &&
					(user?.isAdmin || isCommunityModerator() || isSelectedChannelModerator()) && (
						<Checkbox
							id="notifyMembers"
							name="notifyMembers"
							label="Notify channel members"
							onChange={(e) => setForm({ ...form, notifyMembers: e.target.checked })}
						/>
					)}
			</div>

			<button
				onClick={handleCreatePost}
				className="mx-auto mt-6 block h-12 w-full rounded-md bg-brand-primary text-lg font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
				disabled={!isFormValid}
			>
				{isLoading ? (
					<Loader2Icon className="mx-auto h-8 w-8 animate-spin text-white" />
				) : mode === "create" ? (
					"Post"
				) : (
					"Update"
				)}
			</button>
		</div>
	);
};

export default CreatePostForm;
